/**
 * SecureEmbed Research References Panel
 *
 * Displays related scholarly papers for AEMO queries.
 * Calls /research/recommend or /research/recommend-from-query.
 */

import React, { useState, useCallback } from 'react';
import {
  FileText, ExternalLink, Search, Loader2, AlertCircle,
  BookOpen, Users, Calendar, Award, Filter, Info, ChevronDown, ChevronUp
} from 'lucide-react';

interface Paper {
  record_id?: string;
  doi?: string;
  title: string;
  authors: string[];
  journal: string;
  publication_date: string;
  citation_count: number;
  topics: string[];
  relevance_score: number;
  full_text_url?: string;
  abstract?: string;
  source?: string;
}

interface ResearchReferencesProps {
  embeddingId?: string;
  query?: string;
  className?: string;
}

const ResearchReferences: React.FC<ResearchReferencesProps> = ({
  embeddingId,
  query: initialQuery,
  className = '',
}) => {
  const [papers, setPapers] = useState<Paper[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery || '');
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [minCitations, setMinCitations] = useState(0);
  const [expandedPaper, setExpandedPaper] = useState<string | null>(null);

  const getCrossDomainUrl = (): string => {
    const config = (window as any).SECUREEMBED_CONFIG;
    if (config?.CROSS_DOMAIN_API_ENDPOINT) return config.CROSS_DOMAIN_API_ENDPOINT;
    return '';
  };

  const fetchPapers = useCallback(async () => {
    if (!searchQuery && !embeddingId) return;

    setLoading(true);
    setError(null);
    setHasSearched(true);

    try {
      const crossDomainUrl = getCrossDomainUrl();
      const token = localStorage.getItem('authToken');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json',
      };
      if (token) headers['Authorization'] = `Bearer ${token}`;

      const url = `${crossDomainUrl}/cross-domain/explore`;
      const body: Record<string, any> = {
        query: searchQuery || embeddingId || '',
        limit: 20,
      };

      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      const data = await response.json();

      if (data.error) {
        setError(data.error);
        setPapers([]);
      } else {
        const nodes = data.nodes || [];
        const paperNodes = nodes.filter((n: any) => n.type === 'paper');
        const results: Paper[] = paperNodes.map((n: any) => ({
          record_id: n.data?.record_id || n.id,
          doi: n.data?.doi || undefined,
          title: n.data?.title || n.label || '',
          authors: n.data?.authors || [],
          journal: n.data?.journal || '',
          publication_date: n.data?.publication_date || '',
          citation_count: n.data?.citation_count || 0,
          topics: n.data?.topics || [],
          relevance_score: n.score || 0,
          full_text_url: n.data?.full_text_url || undefined,
          abstract: n.data?.abstract || '',
          source: n.data?.source || '',
        }));
        const filtered = minCitations > 0
          ? results.filter(p => p.citation_count >= minCitations)
          : results;
        setPapers(filtered);
      }
    } catch (err: any) {
      setError('Failed to fetch research references. The cross-domain API may not be available.');
      setPapers([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, embeddingId, minCitations]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') fetchPapers();
  };

  const formatAuthors = (authors: string[]): string => {
    if (!authors || authors.length === 0) return 'Unknown';
    if (authors.length <= 3) return authors.join(', ');
    return `${authors.slice(0, 3).join(', ')} et al.`;
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 shadow-sm ${className}`}>
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BookOpen className="w-5 h-5 text-emerald-600" />
            <h3 className="text-lg font-semibold text-gray-900">Research References</h3>
          </div>
          <div className="group relative">
            <Info className="w-4 h-4 text-gray-400 cursor-help" />
            <div className="absolute right-0 top-6 w-72 p-3 bg-gray-800 text-white text-xs rounded-lg shadow-lg opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
              Search for related academic papers using semantic similarity. Papers are sourced from OpenAlex, Semantic Scholar, and CORE.
            </div>
          </div>
        </div>
        <p className="text-sm text-gray-500 mt-1">Find academic papers related to your energy data queries</p>
      </div>

      {/* Search Bar */}
      <div className="px-6 py-3 border-b border-gray-50">
        <div className="flex items-center space-x-2">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search scholarly papers (e.g., electricity price forecasting)"
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
            />
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border transition-colors ${showFilters ? 'bg-emerald-50 border-emerald-200 text-emerald-600' : 'border-gray-200 text-gray-400 hover:text-gray-600'}`}
          >
            <Filter className="w-4 h-4" />
          </button>
          <button
            onClick={fetchPapers}
            disabled={loading || (!searchQuery && !embeddingId)}
            className="px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-1"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            <span>Search</span>
          </button>
        </div>

        {/* Filters */}
        {showFilters && (
          <div className="mt-3 flex items-center space-x-4">
            <label className="flex items-center space-x-2 text-sm text-gray-600">
              <span>Min Citations:</span>
              <input
                type="number"
                value={minCitations}
                onChange={(e) => setMinCitations(parseInt(e.target.value) || 0)}
                className="w-20 px-2 py-1 border border-gray-200 rounded text-sm"
                min={0}
              />
            </label>
          </div>
        )}
      </div>

      {/* Results */}
      <div className="px-6 py-4">
        {loading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
            <span className="ml-2 text-sm text-gray-500">Searching scholarly papers...</span>
          </div>
        )}

        {error && (
          <div className="flex items-center space-x-2 py-4 text-amber-600 bg-amber-50 px-4 rounded-lg">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {!loading && !error && hasSearched && papers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">No matching papers found. Try a broader search term.</p>
          </div>
        )}

        {!loading && !hasSearched && (
          <div className="text-center py-8 text-gray-400">
            <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Enter a search query to find related research papers</p>
          </div>
        )}

        {papers.length > 0 && (
          <div className="space-y-3">
            <p className="text-xs text-gray-400 mb-2">{papers.length} papers found</p>
            {papers.map((paper, index) => {
              const paperId = paper.doi || paper.record_id || `paper-${index}`;
              const isExpanded = expandedPaper === paperId;
              return (
                <div
                  key={paperId}
                  className="border border-gray-100 rounded-lg p-4 hover:border-emerald-200 hover:bg-emerald-50/30 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-gray-900 line-clamp-2">
                        {paper.full_text_url ? (
                          <a href={paper.full_text_url} target="_blank" rel="noopener noreferrer"
                             className="hover:text-emerald-600 transition-colors">
                            {paper.title}
                            <ExternalLink className="w-3 h-3 inline-block ml-1 opacity-50" />
                          </a>
                        ) : paper.doi ? (
                          <a href={`https://doi.org/${paper.doi}`} target="_blank" rel="noopener noreferrer"
                             className="hover:text-emerald-600 transition-colors">
                            {paper.title}
                            <ExternalLink className="w-3 h-3 inline-block ml-1 opacity-50" />
                          </a>
                        ) : (
                          paper.title
                        )}
                      </h4>

                      <div className="flex items-center space-x-3 mt-1.5 text-xs text-gray-500">
                        <span className="flex items-center">
                          <Users className="w-3 h-3 mr-1" />
                          {formatAuthors(paper.authors)}
                        </span>
                        <span className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {paper.publication_date || 'N/A'}
                        </span>
                        {paper.citation_count > 0 && (
                          <span className="flex items-center">
                            <Award className="w-3 h-3 mr-1" />
                            {paper.citation_count} citations
                          </span>
                        )}
                      </div>

                      {paper.journal && paper.journal !== 'Unknown' && (
                        <p className="text-xs text-gray-400 mt-1 italic">{paper.journal}</p>
                      )}

                      {paper.topics && paper.topics.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {paper.topics.slice(0, 4).map((topic, i) => (
                            <span key={i} className="px-2 py-0.5 bg-emerald-50 text-emerald-700 text-xs rounded-full">
                              {topic}
                            </span>
                          ))}
                          {paper.topics.length > 4 && (
                            <span className="px-2 py-0.5 text-gray-400 text-xs">
                              +{paper.topics.length - 4} more
                            </span>
                          )}
                        </div>
                      )}

                      {/* Expandable abstract */}
                      {paper.abstract && (
                        <button
                          onClick={() => setExpandedPaper(isExpanded ? null : paperId)}
                          className="flex items-center space-x-1 mt-2 text-xs text-emerald-600 hover:text-emerald-700"
                        >
                          {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          <span>{isExpanded ? 'Hide abstract' : 'Show abstract'}</span>
                        </button>
                      )}
                      {isExpanded && paper.abstract && (
                        <p className="mt-2 text-xs text-gray-600 leading-relaxed bg-gray-50 p-3 rounded">
                          {paper.abstract.substring(0, 500)}
                          {paper.abstract.length > 500 && '...'}
                        </p>
                      )}
                    </div>

                    {/* Relevance score badge */}
                    <div className="ml-3 flex-shrink-0">
                      <div className={`px-2 py-1 rounded text-xs font-medium ${
                        paper.relevance_score > 0.8
                          ? 'bg-emerald-100 text-emerald-700'
                          : paper.relevance_score > 0.6
                          ? 'bg-yellow-100 text-yellow-700'
                          : 'bg-gray-100 text-gray-600'
                      }`}>
                        {(paper.relevance_score * 100).toFixed(0)}%
                      </div>
                      {paper.source && (
                        <p className="text-xs text-gray-400 mt-1 text-center">{paper.source}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResearchReferences;
